/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {
    Box,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton
} from '@mui/material';
import Close from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

import Modal from './modal/modal';
import Typography from './typography/typography';
import Button from './button/button';
import ModalLoader from './loaders/modal-loader';
import { DynamicInput, DynamicSearchSelect, DynamicToggleButton, IModalComponentProps } from './form-control';

interface IFormValues {
    designation: {
        company_id: number | null;
        designation_code: string;
        name: string;
        department_id: number | null;
        reports_to: string | null;
        level: string;
        status: boolean;
        description: string;
    };
}

interface IDesignationAddModalProps extends IModalComponentProps {
    initialValues?: {
        company_id?: number | null;
        department_id?: number | null;
        parent_designation_id?: number | null;
    };
}

const designationValidationSchema = Yup.object().shape({
    designation: Yup.object().shape({
        company_id: Yup.number().nullable().required('Company is required'),
        designation_code: Yup.string(),
        name: Yup.string().required('Designation Name is required'),
        department_id: Yup.number().nullable().required('Department is required'),
        reports_to: Yup.string().nullable(),
        level: Yup.number().nullable().typeError('Level must be a number'),
        status: Yup.boolean(),
        description: Yup.string(),
    })
});

const getDefaultValues = (initialValues?: IDesignationAddModalProps['initialValues']) => ({
    designation: {
        company_id: initialValues?.company_id ?? null,
        designation_code: '',
        name: '',
        department_id: initialValues?.department_id ?? null,
        reports_to: initialValues?.parent_designation_id ?? null,
        level: null,
        status: true,
        description: '',
    }
});

export const DesignationAddModal: React.FC<IDesignationAddModalProps> = ({
    isOpen,
    setIsOpen,
    onSave,
    isLoading,
    modalProps,
    initialValues,
}) => {
    const { t } = useTranslation();

    const { control, handleSubmit, reset, trigger, watch } = useForm<any>({
        mode: 'all',
        resolver: yupResolver(designationValidationSchema),
        defaultValues: getDefaultValues(initialValues)
    });

    const rootCompanyId = watch('designation.company_id');
    const rootDepartmentId = watch('designation.department_id');

    useEffect(() => {
        if (isOpen) {
            reset(getDefaultValues(initialValues));
        }
    }, [isOpen]);

    const handleModalClose = () => {
        reset(getDefaultValues(initialValues));
        setIsOpen();
    };

    const onSubmit = async (values: IFormValues) => {
        const isValid = await trigger();
        if (!isValid) return;
        await onSave(values as any);
        handleModalClose();
    };

    return (
        <Modal
            open={isOpen}
            onClose={handleModalClose}
            maxWidth='sm'
            fullWidth
            {...modalProps}
        >
            <>
                <form onSubmit={handleSubmit(onSubmit)}>
                    {isLoading && <ModalLoader />}
                    <DialogTitle className='brandAddModal--DialogTitle'>
                        <Typography type='s3' color='inherit' weight='medium'>
                            {t('hrms.organization_structure.designationModal.title', 'Add Designation')}
                        </Typography>
                        <IconButton onClick={handleModalClose}>
                            <Close fontSize='small' />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent className='brandAddModal--DialogContents'>
                        <Grid container rowGap={1.5} columnSpacing={3} pt={3}>
                            {/* Company - Required */}
                            <Grid item sm={12}>
                                <DynamicSearchSelect
                                    label={t('hrms.organization_structure.designationModal.company_label', 'Company')}
                                    name='company_id'
                                    placeholder={t('hrms.organization_structure.designationModal.company_placeholder', 'Select Company')}
                                    fieldArrayName='designation'
                                    formControl={control as any}
                                    apiType='company'
                                    required
                                    options={[]}
                                />
                            </Grid>

                            {/* Department - Required */}
                            <Grid item sm={12}>
                                <DynamicSearchSelect
                                    label={t('hrms.organization_structure.designationModal.department_label', 'Department')}
                                    name='department_id'
                                    placeholder={t('hrms.organization_structure.designationModal.department_placeholder', 'Select Department')}
                                    fieldArrayName='designation'
                                    formControl={control as any}
                                    apiType='department'
                                    required
                                    options={[]}
                                    customeFilter={{
                                        '&company_id.eq': rootCompanyId || 0,
                                        // '&is_active.eq': 1
                                    }}
                                />
                            </Grid>

                            {/* Designation Code */}
                            <Grid item sm={6}>
                                <DynamicInput
                                    type='text'
                                    formControl={control}
                                    label={t('hrms.organization_structure.designationModal.code_label', 'Designation Code')}
                                    name='designation_code'
                                    fieldArrayName='designation'
                                    placeholder={t('hrms.organization_structure.designationModal.code_placeholder', 'Enter Designation Code')}
                                />
                            </Grid>

                            {/* Designation Name - Required */}
                            <Grid item sm={6}>
                                <DynamicInput
                                    type='text'
                                    formControl={control}
                                    label={t('hrms.organization_structure.designationModal.name_label', 'Designation Name')}
                                    name='name'
                                    fieldArrayName='designation'
                                    placeholder={t('hrms.organization_structure.designationModal.name_placeholder', 'Enter Designation Name')}
                                    required
                                />
                            </Grid>

                            {/* Reports To (Parent Designation) - Optional */}
                            <Grid item sm={6}>
                                <DynamicSearchSelect
                                    label={t('hrms.organization_structure.designationModal.reports_to_label', 'Reports To')}
                                    name='reports_to'
                                    placeholder={t('hrms.organization_structure.designationModal.reports_to_placeholder', 'Select Reports To')}
                                    fieldArrayName='designation'
                                    formControl={control as any}
                                    apiType='designation'
                                    required={false}
                                    options={[]}
                                    customeFilter={{
                                        '&company_id.eq': rootCompanyId || 0,
                                        '&department_id.eq': rootDepartmentId || 0,
                                    }}
                                />
                            </Grid>

                            {/* Level - Optional */}
                            <Grid item sm={6}>
                                <DynamicInput
                                    type='number'
                                    formControl={control}
                                    label={t('hrms.organization_structure.designationModal.level_label', 'Level')}
                                    name='level'
                                    fieldArrayName='designation'
                                    disabled
                                    placeholder={t('hrms.organization_structure.designationModal.level_placeholder', 'Enter Level')}
                                />
                            </Grid>

                            {/* Status Toggle */}
                            <Grid item sm={6}>
                                <DynamicToggleButton
                                    formControl={control as any}
                                    name='status'
                                    fieldArrayName='designation'
                                    label={t('hrms.organization_structure.designationModal.status_label', 'Status')}
                                    title={t('common.active', 'Active')}
                                    titlePosition='end'
                                    position={3}
                                />
                            </Grid>

                            {/* Description - Optional, full width */}
                            <Grid item sm={12}>
                                <DynamicInput
                                    type='textarea'
                                    formControl={control}
                                    label={t('hrms.organization_structure.designationModal.description_label', 'Description')}
                                    name='description'
                                    fieldArrayName='designation'
                                    placeholder={t('hrms.organization_structure.designationModal.description_placeholder', 'Enter Description')}
                                    is_multiline
                                    rows={3}
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions className='brandAddModal--DialogAction'>
                        <span></span>
                        <Box display='flex' gap={1.5}>
                            <Button
                                className='brandAddModal--DialogAction--WhiteBtn'
                                onClick={handleModalClose}
                            >
                                {t('common.cancel')}
                            </Button>
                            <Button type='button' onClick={handleSubmit(onSubmit)}>
                                {t('common.save')}
                            </Button>
                        </Box>
                    </DialogActions>
                </form>
            </>
        </Modal>
    );
};
